import React, { useState, useEffect, useRef, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { taskAPI, timeLogAPI, timerAPI, commentAPI, fileAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTaskWebSocket } from '../hooks/useTaskWebSocket';
import {
    Dialog,
    DialogContent,
    DialogHeader,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input, ResizableInput } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ScrollArea } from '../components/ui/scroll-area';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { cn, formatDate, formatDateTime, formatDuration, priorityConfig, getInitials } from '../lib/utils';
import RichTextEditor from './RichTextEditor';
import OnlyOfficeEditorModal from './OnlyOfficeEditorModal';
import api from '../lib/api';
import {
    X,
    Calendar as CalendarIcon,
    Users,
    Tag,
    Plus,
    Trash2,
    Play,
    Square,
    Loader2,
    Flag,
    MessageSquare,
    Edit2,
    Send,
    Paperclip,
    FileImage,
    FileVideo,
    File,
    Upload,
    Check,
    Search,
    ChevronRight,
    ChevronLeft,
    ListTree,
    CircleDot,
    Copy,
    Link,
    Bug,
    Cloud,
    CloudOff,
    Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, set } from 'date-fns';
import { DialogTitle } from '@radix-ui/react-dialog';
import InputComment from './InputComment';
import CommentRenderer from './CommentRenderer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { listAPI } from '../lib/api';
import FieldStatus from './Field/FieldStatus';
import { useTimerContext } from '@/context/TimerContext';

// Debounce hook for auto-save
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        
        return () => clearTimeout(handler);
    }, [value, delay]);
    
    return debouncedValue;
};

// Multi-Select Assignee Dropdown Component
const AssigneeMultiSelect = ({ members, selectedIds, onChange, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredMembers = members.filter(member => {
        const name = member.user_name || member.name || '';
        return name.toLowerCase().includes(searchQuery.toLowerCase());
    });

    const toggleMember = (memberId) => {
        if (disabled) return;
        if (selectedIds.includes(memberId)) {
            onChange(selectedIds.filter(id => id !== memberId));
        } else {
            onChange([...selectedIds, memberId]);
        }
    };

    const getMemberName = (member) => member.user_name || member.name || 'Unknown';
    const getMemberId = (member) => member.user_id || member.id;

    const selectedMembers = members.filter(m => selectedIds.includes(getMemberId(m)));


    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={cn(
                    "flex items-center gap-2 min-h-[32px] px-2 py-1 text-sm",
                    "hover:bg-brand-accent-soft rounded transition-colors",
                    disabled && "opacity-50 cursor-not-allowed"
                )}
            >
                {selectedMembers.length > 0 ? (
                    <div className="flex items-center gap-1">
                        {selectedMembers.slice(0, 3).map(member => (
                            <div 
                                key={getMemberId(member)} 
                                className="w-6 h-6 rounded-full bg-brand-primary text-white text-xs flex items-center justify-center"
                                title={getMemberName(member)}
                            >
                                {getInitials(getMemberName(member))}
                            </div>
                        ))}
                        {selectedMembers.length > 3 && (
                            <span className="text-xs text-brand-text-muted">+{selectedMembers.length - 3}</span>
                        )}
                    </div>
                ) : (
                    <span className="text-brand-text-muted">Empty</span>
                )}
            </button>

            {isOpen && (
                <div className="absolute z-50 top-full left-0 mt-1 bg-white border border-brand-border rounded-md shadow-lg min-w-[280px]">
                    <div className="p-2 border-b border-brand-border">
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-text-muted" />
                            <Input
                                type="text"
                                placeholder="Search members..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-8 h-8 text-sm"
                                autoFocus
                            />
                        </div>
                    </div>

                    <ScrollArea className="max-h-[200px]">
                        <div className="p-1">
                            {filteredMembers.length > 0 ? filteredMembers.map(member => {
                                const memberId = getMemberId(member);
                                const memberName = getMemberName(member);
                                const isSelected = selectedIds.includes(memberId);

                                return (
                                    <div
                                        key={memberId}
                                        className={cn(
                                            'flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors',
                                            isSelected ? 'bg-brand-primary/10' : 'hover:bg-brand-accent-soft'
                                        )}
                                        onClick={() => toggleMember(memberId)}
                                    >
                                        <div className={cn(
                                            "w-7 h-7 rounded-full flex items-center justify-center text-xs",
                                            isSelected ? "bg-brand-primary text-white" : "bg-brand-accent-soft"
                                        )}>
                                            {getInitials(memberName)}
                                        </div>
                                        <span className="flex-1 text-sm">{memberName}</span>
                                        {isSelected && <Check className="w-4 h-4 text-brand-primary" />}
                                    </div>
                                );
                            }) : (
                                <p className="text-sm text-brand-text-muted text-center py-4">No members</p>
                            )}
                        </div>
                    </ScrollArea>
                </div>
            )}
        </div>
    );
};

// Component to render description with clickable links and embedded images
const RichDescription = ({ content }) => {
    if (!content) return null;
    
    // Regex patterns
    const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
    const imageUrlRegex = /https?:\/\/[^\s<]+\.(jpg|jpeg|png|gif|webp|svg)(\?[^\s<]*)?/gi;
    
    // Split content into parts
    const parts = [];
    let lastIndex = 0;
    const matches = [...content.matchAll(urlRegex)];
    
    matches.forEach((match, idx) => {
        const url = match[0];
        const startIndex = match.index;
        
        // Add text before this URL
        if (startIndex > lastIndex) {
            parts.push({
                type: 'text',
                content: content.slice(lastIndex, startIndex)
            });
        }
        
        // Check if it's an image URL
        if (imageUrlRegex.test(url)) {
            parts.push({
                type: 'image',
                content: url
            });
        } else {
            parts.push({
                type: 'link',
                content: url
            });
        }
        
        lastIndex = startIndex + url.length;
        // Reset regex
        imageUrlRegex.lastIndex = 0;
    });
    
    // Add remaining text
    if (lastIndex < content.length) {
        parts.push({
            type: 'text',
            content: content.slice(lastIndex)
        });
    }
    
    if (parts.length === 0) {
        parts.push({ type: 'text', content });
    }
    
    return (
        <div className="prose prose-sm max-w-none whitespace-pre-wrap break-words">
            {parts.map((part, idx) => {
                if (part.type === 'image') {
                    return (
                        <div key={idx} className="my-2">
                            <a href={part.content} target="_blank" rel="noopener noreferrer">
                                <img 
                                    src={part.content} 
                                    alt="Embedded" 
                                    className="max-w-full max-h-64 rounded-lg border border-brand-border"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </a>
                        </div>
                    );
                } else if (part.type === 'link') {
                    return (
                        <a 
                            key={idx}
                            href={part.content} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-brand-primary hover:underline break-all"
                        >
                            {part.content}
                        </a>
                    );
                } else {
                    // Preserve line breaks in text
                    return part.content.split('\n').map((line, lineIdx) => (
                        <React.Fragment key={`${idx}-${lineIdx}`}>
                            {lineIdx > 0 && <br />}
                            {line}
                        </React.Fragment>
                    ));
                }
            })}
        </div>
    );
};

export const TaskDetailFullModal = ({ 
    isOpen, 
    onClose, 
    task: initialTask, 
    listId, 
    statuses = [], 
    members = [],
    onTaskUpdate,
    readOnly = false,
    isDeletedTask = false
}) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
    
    // Current task being viewed (can be subtask)
    const [currentTask, setCurrentTask] = useState(initialTask);
    const [taskHistory, setTaskHistory] = useState([]); // Stack for navigation
    
    // Form state
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [status, setStatus] = useState('To Do');
    const [priority, setPriority] = useState('none');
    const [category, setCategory] = useState('');
    const [dueDate, setDueDate] = useState(null);
    // Reason required when CHANGING an existing due_date (not when first-time setting)
    const [dueDateChangeReason, setDueDateChangeReason] = useState('');
    const [showDueDateReasonDialog, setShowDueDateReasonDialog] = useState(false);
    const [pendingDueDate, setPendingDueDate] = useState(null);
    const [reasonDraft, setReasonDraft] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [timeEstimate, setTimeEstimate] = useState('');
    const [tags, setTags] = useState([]);
    const [assigneeIds, setAssigneeIds] = useState([]);
    const [newTag, setNewTag] = useState('');
    const [checklist, setChecklist] = useState([]);
    const [newChecklistItem, setNewChecklistItem] = useState('');
    const [listStatus, setListStatus] = useState(statuses);
    
    // Task categories (loaded from workspace settings)
    const [taskCategories, setTaskCategories] = useState(['BUG', 'TASK', 'CHANGE_REQUEST', 'IMPROVEMENT', 'FEATURE']);
    
    // Reviewers
    const [reviewerIds, setReviewerIds] = useState([]);
    
    // Subtasks
    const [subtasks, setSubtasks] = useState([]);
    const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
    const [parentTask, setParentTask] = useState(null);
    
    // Time logs
    const [timeLogs, setTimeLogs] = useState([]);
    
    // Comments
    const [activityLogs, setActivityLogs] = useState([]);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [rawNewComment, setRawNewComment] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editCommentText, setEditCommentText] = useState('');
    const [commentAttachments, setCommentAttachments] = useState([]);
    
    // File attachments
    const [attachments, setAttachments] = useState([]);
    // OnlyOffice
    const [officeFileId, setOfficeFileId] = useState(null);
    const [officeEnabled, setOfficeEnabled] = useState(false);
    const [officeAllowed, setOfficeAllowed] = useState(['docx', 'xlsx', 'pptx']);

    useEffect(() => {
        // Fetch global OnlyOffice availability once per mount
        (async () => {
            try {
                const r = await api.get('/onlyoffice/status');
                setOfficeEnabled(!!r.data?.enabled);
                if (r.data?.allowed_file_types) setOfficeAllowed(r.data.allowed_file_types);
            } catch (e) { /* silently ignore */ }
        })();
    }, []);
    const [uploadingFile, setUploadingFile] = useState(false);
    const fileInputRef = useRef(null);
    
    // Bug-specific fields
    const [rootCause, setRootCause] = useState('');
    const [mitigations, setMitigations] = useState('');
    const [similarBugs, setSimilarBugs] = useState([]);
    const [showBugAnalysis, setShowBugAnalysis] = useState(false);
    
    // Active timer
    const {activeTimer, setActiveTimer, checkActiveTimer} = useTimerContext();
    const [timerLoading, setTimerLoading] = useState(false);

    const [mentions, setMentions] = useState([]);
    const inputCommentRef = useRef(null);


    // WebSocket for real-time auto-save
    const handleFieldUpdate = useCallback((field, value, updatedBy) => {
        // Another user updated this field - update local state
        if (updatedBy !== user?.id) {
            switch (field) {
                case 'title': setTitle(value); break;
                case 'description': setDescription(value); break;
                case 'status': setStatus(value); break;
                // For DAILY_TASK, also update daily_status_history in currentTask
                // so the Daily Status tab reflects the change
                case 'priority': setPriority(value); break;
                case 'category': setCategory(value); break;
                case 'tags': setTags(value || []); break;
                case 'assignee_ids': setAssigneeIds(value || []); break;
                case 'reviewer_ids': setReviewerIds(value || []); break;
                case 'checklist': setChecklist(value || []); break;
                default: break;
            }
            toast.info(`Field "${field}" updated by another user`);
        }
    }, [user?.id]);

    const { 
        isConnected: wsConnected, 
        lastUpdate,
        activeUsers,
        typingUsers,
        sendUpdate: wsSendUpdate,
        sendComment: wsSendComment,
        sendCommentUpdate: wsSendCommentUpdate,
        sendCommentDelete: wsSendCommentDelete,
        // sendTyping: wsSendTyping
    } = useTaskWebSocket(currentTask?.id, {
        enableWebSocket: autoSaveEnabled && !readOnly,
        onFieldUpdate: handleFieldUpdate,
            onCommentAdded: (comment) => {
            // Add comment from other users in real-time
            setComments(prev => {
                // Avoid duplicates (in case we just sent this comment ourselves)
                if (prev.find(c => c.id === comment.id)) return prev;
                return [...prev, comment];
            });
        },
        onCommentUpdated: (updatedComment) => {
            setComments(prev => prev.map(c => c.id === updatedComment.id ? updatedComment : c));
        },
        onCommentDeleted: (commentId) => {
            setComments(prev => prev.filter(c => c.id !== commentId));
        }
    });

    const [activeTab, setActiveTab] = useState('comments');
    // Debounced values for auto-save (1 second delay)
    const debouncedTitle = useDebounce(title, 1000);
    const debouncedDescription = useDebounce(description, 1500);

    const changesLogsToArray = (obj) => {
        return Object.keys(obj).map((key) => ({
                key,
                value: obj[key],
            }
        ));
    };
    const filteredChanges = (obj) =>{
        return changesLogsToArray(obj).filter((change) => 
            change.key !== 'status' ? JSON.stringify(change.value) !== JSON.stringify(currentTask[change.key])
            : change.value
    )};


    
    // Track if initial load is done to prevent auto-save on first load
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    const prevValuesRef = useRef({});

    // Auto-save via WebSocket when debounced values change
    useEffect(() => {
        if (!initialLoadDone || !wsConnected || !autoSaveEnabled || !currentTask?.id) return;
        
        // Check if title actually changed
        if (debouncedTitle && debouncedTitle !== prevValuesRef.current.title) {
            wsSendUpdate('title', debouncedTitle);
            prevValuesRef.current.title = debouncedTitle;
        }
    }, [debouncedTitle, initialLoadDone, wsConnected, autoSaveEnabled, currentTask?.id, wsSendUpdate]);

    useEffect(() => {
        if (!initialLoadDone || !wsConnected || !autoSaveEnabled || !currentTask?.id) return;
        
        // Check if description actually changed
        if (debouncedDescription !== prevValuesRef.current.description) {
            wsSendUpdate('description', debouncedDescription);
            prevValuesRef.current.description = debouncedDescription;
        }
    }, [debouncedDescription, initialLoadDone, wsConnected, autoSaveEnabled, currentTask?.id, wsSendUpdate]);

    // Instant save for dropdowns and selects (status, priority, category, assignees)
    const handleInstantSave = useCallback((field, value) => {
        if (!wsConnected || !autoSaveEnabled || !currentTask?.id) return;
        wsSendUpdate(field, value);
    }, [wsConnected, autoSaveEnabled, currentTask?.id, wsSendUpdate]);

    // Reset currentTask when initialTask changes
    useEffect(() => {
        if (isOpen && initialTask) {
            setCurrentTask(initialTask);
            setTaskHistory([]);
            setInitialLoadDone(false);
        }
    }, [isOpen, initialTask]);

    useEffect(() => {
        if (isOpen && currentTask) {
            loadTaskData(currentTask);
        }
    }, [isOpen, currentTask]);

    const fetchComments = async () => {
            // Load comments
        const commentsRes = await commentAPI.getByTask(currentTask.id);
        setComments(commentsRes.data);
    }
    const fetchActivityLogs = async () => {
        const activityRes = await taskAPI.getLogs(currentTask.id);
        setActivityLogs(activityRes.data);
    }

    const fetchListStatus = async (id) => {
        const listRes = await listAPI.get(id);
        setListStatus(listRes.data.statuses);
    }

    const loadTaskData = async (taskToLoad) => {
        setLoading(true);
        setInitialLoadDone(false);
        try {
            fetchListStatus(taskToLoad.list_id);
            setTitle(taskToLoad.title || '');
            setDescription(taskToLoad.description || '');
            setIsEditingDescription(false); // Reset edit mode when loading new task
            setStatus(taskToLoad.status || 'None');

            // For DAILY_TASK: override status with today's entry from daily_status_history
            if (taskToLoad.category === 'DAILY_TASK' && taskToLoad.daily_status_history) {
                const todayStr = new Date().toLocaleDateString('sv-SE'); // YYYY-MM-DD
                const todayEntry = taskToLoad.daily_status_history.find(e => e.date === todayStr);
                if (todayEntry) {
                    setStatus(todayEntry.status);
                } else {
                    setStatus('To Do');
                }
            }

            setPriority(taskToLoad.priority || 'none');
            setCategory(taskToLoad.category || '');
            setDueDate(taskToLoad.due_date ? new Date(taskToLoad.due_date) : null);
            setDueDateChangeReason('');
            setPendingDueDate(null);
            setShowDueDateReasonDialog(false);
            setReasonDraft('');
            setStartDate(taskToLoad.start_date ? new Date(taskToLoad.start_date) : null);
            setTimeEstimate(taskToLoad.time_estimate ? String(taskToLoad.time_estimate) : '');
            setTags(taskToLoad.tags || []);
            setChecklist(taskToLoad.checklist || []);
            setAssigneeIds(taskToLoad.assignee_ids || []);
            setReviewerIds(taskToLoad.reviewer_ids || []);
            setAttachments(taskToLoad.attachments || []);
            
            // Bug-specific fields
            setRootCause(taskToLoad.root_cause || '');
            setMitigations(taskToLoad.mitigations || '');
            setSimilarBugs(taskToLoad.similar_bugs || []);
            setShowBugAnalysis(taskToLoad.category === 'BUG' && (taskToLoad.similar_bugs?.length > 0 || taskToLoad.root_cause || taskToLoad.mitigations));
            
            // Store initial values for comparison
            prevValuesRef.current = {
                title: taskToLoad.title || '',
                description: taskToLoad.description || '',
                status: taskToLoad.status || 'To Do',
                priority: taskToLoad.priority || 'none',
                category: taskToLoad.category || ''
            };

            // Load subtasks
            if(isDeletedTask){
                const subtasksRes = await taskAPI.getDeletedSubtasks(taskToLoad.id);
                setSubtasks(subtasksRes.data);
            }else{
                const subtasksRes = await taskAPI.getSubtasks(taskToLoad.id);
                setSubtasks(subtasksRes.data);
            }

            // Load parent task if exists
            if (taskToLoad.parent_task_id) {
                try {
                    const parentRes = await taskAPI.get(taskToLoad.parent_task_id);
                    setParentTask(parentRes.data);
                } catch (e) {
                    setParentTask(null);
                }
            } else {
                setParentTask(null);
            }

            // Load time logs
            const logsRes = await timeLogAPI.getByTask(taskToLoad.id);
            setTimeLogs(logsRes.data);

            fetchComments();
            fetchActivityLogs();

            // Check active timer
            // checkActiveTimer();
            
            // Mark initial load as done after a short delay
            setTimeout(() => setInitialLoadDone(true), 500);
        } catch (error) {
            console.error('Failed to load task data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Copy link handler
    const handleCopyLink = () => {
        const url = `${window.location.origin}/share/task/${currentTask.id}`;
        navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard');
    };

    // Navigate to subtask
    const handleSubtaskClick = async (subtask) => {
        // Save current task to history
        setTaskHistory(prev => [...prev, currentTask]);
        // Load subtask details
        try {
            const res = await taskAPI.get(subtask.id);
            setCurrentTask(res.data);
        } catch (error) {
            toast.error('Failed to load subtask');
        }
    };

    // Navigate back to parent
    const handleGoBack = () => {
        if (taskHistory.length > 0) {
            const previousTask = taskHistory[taskHistory.length - 1];
            setTaskHistory(prev => prev.slice(0, -1));
            setCurrentTask(previousTask);
        }
    };

    // Navigate to parent task
    const handleParentClick = async () => {
        if (parentTask) {
            setTaskHistory(prev => [...prev, currentTask]);
            setCurrentTask(parentTask);
        }
    };

    const handleSave = async () => {
        if (!title.trim()) {
            toast.error('Task title is required');
            return;
        }
        
        // Validation for BUG category
        if (category === 'BUG' && (status === 'Done' || status === 'Review')) {
            if (!rootCause?.trim() || !mitigations?.trim()) {
                toast.error('Root Cause dan Mitigations wajib diisi untuk Bug yang sudah resolved (Done/Review)');
                return;
            }
        }

        setSaving(true);
        try {
            const taskData = {
                title: title.trim(),
                description: description || null,
                status,
                priority,
                category: category || null,
                start_date: startDate ? format(startDate, 'yyyy-MM-dd') : null,
                due_date: dueDate ? format(dueDate, 'yyyy-MM-dd') : null,
                due_date_change_reason: dueDateChangeReason || undefined,
                tags: tags.length > 0 ? tags : [],
                checklist: checklist.length > 0 ? checklist : [],
                assignee_ids: assigneeIds,
                reviewer_ids: reviewerIds,
                attachments: attachments,
            };
            
            // Add bug-specific fields
            if (category === 'BUG') {
                taskData.root_cause = rootCause;
                taskData.mitigations = mitigations;
            }

            await taskAPI.update(currentTask.id, taskData);
            toast.success('Task updated');
            setDueDateChangeReason('');
            if (onTaskUpdate) onTaskUpdate();
        } catch (error) {
            console.error('Failed to save task:', error);
            const errorMsg = error.response?.data?.detail || 'Failed to save task';
            toast.error(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Delete this task?')) return;
        try {
            await taskAPI.delete(currentTask.id);
            toast.success('Task deleted');
            // If we deleted a subtask, go back to parent
            if (taskHistory.length > 0) {
                handleGoBack();
            } else {
                onClose();
            }
            if (onTaskUpdate) onTaskUpdate();
        } catch (error) {
            toast.error('Failed to delete task');
        }
    };

    const handleDuplicate = async () => {
        try {
            await taskAPI.duplicate(currentTask.id);
            toast.success('Task duplicated');
            if (onTaskUpdate) onTaskUpdate();
        } catch (error) {
            toast.error('Failed to duplicate task');
        }
    };

    // Subtask handlers
    const handleAddSubtask = async () => {
        if (!newSubtaskTitle.trim()) return;
        try {
            await taskAPI.create({
                title: newSubtaskTitle,
                list_id: currentTask.list_id,
                parent_task_id: currentTask.id,
                status: 'To Do',
                priority: 'none',
            });
            setNewSubtaskTitle('');
            const subtasksRes = await taskAPI.getSubtasks(currentTask.id);
            setSubtasks(subtasksRes.data);
            toast.success('Subtask added');
        } catch (error) {
            console.error('Subtask error:', error);
            let errorMessage = 'Failed to add subtask';
            if (error.response?.data) {
                const data = error.response.data;
                if (typeof data.detail === 'string') {
                    errorMessage = data.detail;
                } else if (Array.isArray(data.detail)) {
                    errorMessage = data.detail.map(e => e.msg || e.message).join(', ');
                } else if (typeof data === 'string') {
                    errorMessage = data;
                }
            }
            toast.error(errorMessage);
        }
    };

    const handleToggleSubtask = async (subtask) => {
        try {
            const newStatus = subtask.status === 'Done' ? 'To Do' : 'Done';
            await taskAPI.update(subtask.id, { status: newStatus });
            const subtasksRes = await taskAPI.getSubtasks(currentTask.id);
            setSubtasks(subtasksRes.data);
        } catch (error) {
            toast.error('Failed to update subtask');
        }
    };

    // Timer handlers
    const handleStartTimer = async () => {
        setTimerLoading(true);
        try {
            const res = await timerAPI.start(currentTask.id);
            setActiveTimer(res.data);
            // setStatus('In Progress');
            toast.success('Timer started');
            if (onTaskUpdate) onTaskUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to start timer');
        } finally {
            setTimerLoading(false);
        }
    };

    const handleStopTimer = async () => {
        setTimerLoading(true);
        try {
            await timerAPI.stop();
            setActiveTimer(null);
            toast.success('Timer stopped');
            const logsRes = await timeLogAPI.getByTask(currentTask.id);
            setTimeLogs(logsRes.data);
            if (onTaskUpdate) onTaskUpdate();
        } catch (error) {
            toast.error('Failed to stop timer');
        } finally {
            setTimerLoading(false);
        }
    };


    const clearComment = () => {
        inputCommentRef.current?.clearInput();
    };
    const handleOnchangeComment = (e) => {
        console.log(e);
        setNewComment(e.plainText);
        setRawNewComment(e.rawText);
        setMentions(e.mentions);
        setCommentAttachments(e.attachments || []);
    }
    // Comment handlers
    const handleAddComment = async () => {
        if (!newComment.trim() && (!commentAttachments || commentAttachments.length === 0)) return;
        const commentContent = rawNewComment;
        const commentMentions = mentions.map(m => m.user_id);
        const commentAtts = commentAttachments || [];

        // Optimistic: clear input immediately
        setNewComment('');
        setCommentAttachments([]);
        clearComment();

        // Try WebSocket first, fallback to HTTP
        const sent = wsSendComment(commentContent, commentMentions, commentAtts);
        if (sent) {
            // WebSocket will broadcast new_comment back to us (and others)
            // No need to add locally - onCommentAdded callback handles it
        } else {
            // Fallback to HTTP
            try {
                setSubmittingComment(true);
                const res = await commentAPI.create({ 
                    task_id: currentTask.id, 
                    content: commentContent, 
                    mentions: commentMentions,
                    attachments: commentAtts
                });
                setComments(prev => [...prev, res.data]);
            } catch (error) {
                toast.error('Failed to add comment');
            } finally {
                setSubmittingComment(false);
            }
        }
    };

    const handleUpdateComment = async (commentId) => {
        if (!editCommentText.trim()) return;
        const newContent = editCommentText;

        // Optimistic local update
        setComments(prev => prev.map(c => c.id === commentId ? { ...c, content: newContent } : c));
        setEditingCommentId(null);
        setEditCommentText('');

        // Try WebSocket first, fallback to HTTP
        const sent = wsSendCommentUpdate(commentId, newContent);
        if (!sent) {
            try {
                await commentAPI.update(commentId, { content: newContent });
            } catch (error) {
                toast.error('Failed to update comment');
                // Revert on failure
                fetchComments();
            }
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (!window.confirm('Delete comment?')) return;

        // Optimistic local delete
        setComments(prev => prev.filter(c => c.id !== commentId));

        // Try WebSocket first, fallback to HTTP
        const sent = wsSendCommentDelete(commentId);
        if (!sent) {
            try {
                await commentAPI.delete(commentId);
            } catch (error) {
                toast.error('Failed to delete comment');
                fetchComments();
            }
        }
    };

    // Tag handlers
    const handleAddTag = () => {
        if (!newTag.trim() || tags.includes(newTag.trim())) return;
        setTags([...tags, newTag.trim()]);
        setNewTag('');
    };

    const removeTag = (tag) => {
        setTags(tags.filter(t => t !== tag));
    };

    // File handlers
    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (file.size > 50 * 1024 * 1024) {
            toast.error('Max 50MB');
            return;
        }
        setUploadingFile(true);
        try {
            const res = await fileAPI.upload(file, currentTask.id);
            setAttachments([...attachments, res.data]);
            toast.success('File uploaded');
        } catch (error) {
            toast.error('Failed to upload');
        } finally {
            setUploadingFile(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const getFileIcon = (fileType) => {
        if (fileType === 'image') return <FileImage className="w-4 h-4" />;
        if (fileType === 'video') return <FileVideo className="w-4 h-4" />;
        return <File className="w-4 h-4" />;
    };

    // Checklist handlers
    const handleAddChecklistItem = () => {
        if (!newChecklistItem.trim()) return;
        setChecklist([...checklist, { text: newChecklistItem.trim(), completed: false }]);
        setNewChecklistItem('');
    };

    const toggleChecklistItem = (index) => {
        const updated = [...checklist];
        updated[index].completed = !updated[index].completed;
        setChecklist(updated);
    };

    const removeChecklistItem = (index) => {
        setChecklist(checklist.filter((_, i) => i !== index));
    };

    if (loading) {
        return (
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="max-w-[95vw] w-full h-[90vh] p-0" hideCloseButton>
                    <DialogTitle className="hidden"></DialogTitle>
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    // Get assignee info for display
    const getAssigneeInfo = (assigneeIds) => {
        if (!assigneeIds || assigneeIds.length === 0) return null;
        return members.filter(m => assigneeIds.includes(m.user_id || m.id));
    };

    function renderChangesValue(value) {
        if (Array.isArray(value)) {
            return value.map(v => typeof v === "string" ? v : v.filename).join(", "); 
        }
        return value; 
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent
                className="w-full max-w-[95vw] lg:max-w-[95vw] h-[95vh] lg:h-[90vh] p-0 !flex !flex-col"
                hideCloseButton
                data-testid="task-detail-full-modal"
                onInteractOutside={(e) => { if (showDueDateReasonDialog) e.preventDefault(); }}
                onEscapeKeyDown={(e) => { if (showDueDateReasonDialog) e.preventDefault(); }}
            >
                {/* Header */}
                <DialogTitle className="hidden"></DialogTitle>
                <div className="flex-shrink-0 flex items-center justify-between px-3 lg:px-4 py-2 lg:py-3 border-b border-brand-border bg-brand-accent-soft/30">
                    <div className="flex items-center gap-2 lg:gap-3">
                        {/* Back button when viewing subtask */}
                        {taskHistory.length > 0 && (
                            <Button variant="ghost" size="icon" onClick={handleGoBack} className="h-8 w-8">
                                <ChevronLeft className="w-5 h-5" />
                            </Button>
                        )}
                        {/* Subtask indicator */}
                        {currentTask?.parent_task_id && (
                            <span className="text-xs px-2 py-0.5 bg-brand-warning/10 text-brand-warning rounded">Subtask</span>
                        )}
                        <span className="text-xs lg:text-sm font-mono text-brand-primary font-semibold">
                            {currentTask?.task_code || `#${currentTask?.task_number}`}
                        </span>
                        <span className="text-xs lg:text-sm text-brand-text-muted hidden sm:inline">
                            Created {formatDate(currentTask?.created_at)}
                        </span>
                        
                        {/* Auto-save indicator */}
                        <div className="flex items-center gap-1.5 ml-2">
                            {wsConnected ? (
                                <div className="flex items-center gap-1 text-xs text-brand-success">
                                    <Cloud className="w-3.5 h-3.5" />
                                    <span className="hidden lg:inline">Auto-save</span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-1 text-xs text-brand-text-muted">
                                    <CloudOff className="w-3.5 h-3.5" />
                                    <span className="hidden lg:inline">Offline</span>
                                </div>
                            )}
                            {lastUpdate?.confirmed && (
                                <span className="text-xs text-brand-success animate-pulse">Saved</span>
                            )}
                        </div>
                        
                        {/* Active users viewing this task */}
                        {activeUsers.length > 0 && (
                            <div className="hidden md:flex items-center gap-1 ml-2">
                                <span className="text-xs text-brand-text-muted">Viewing:</span>
                                <div className="flex -space-x-1">
                                    {activeUsers.slice(0, 3).map((u, idx) => (
                                        <div 
                                            key={u.user_id} 
                                            className="w-5 h-5 rounded-full bg-brand-primary text-white text-[10px] flex items-center justify-center border border-white"
                                            title={u.user_name}
                                        >
                                            {getInitials(u.user_name)}
                                        </div>
                                    ))}
                                    {activeUsers.length > 3 && (
                                        <div className="w-5 h-5 rounded-full bg-gray-300 text-[10px] flex items-center justify-center border border-white">
                                            +{activeUsers.length - 3}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-1 lg:gap-2">
                        {/* Copy Link Button */}
                        <Button variant="ghost" size="sm" onClick={handleCopyLink} className="hidden sm:flex">
                            <Link className="w-4 h-4 mr-1" /> <span className="hidden lg:inline">Copy Link</span>
                        </Button>
                        
                        {/* Start/Stop Timer Button */}
                        {!readOnly && (
                            activeTimer ? (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleStopTimer}
                                    disabled={timerLoading}
                                    className="text-red-500 hover:text-red-600 hover:bg-red-50 hidden sm:flex"
                                >
                                    {timerLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Square className="w-4 h-4 mr-1" />}
                                    <span className="hidden lg:inline">Stop Timer</span>
                                </Button>
                            ) : (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleStartTimer}
                                    disabled={timerLoading}
                                    className="text-brand-primary hover:bg-brand-primary/10 hidden sm:flex"
                                >
                                    {timerLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                                    <span className="hidden lg:inline">Start Timer</span>
                                </Button>
                            )
                        )}
                        
                        {!readOnly && (
                            <>
                                <Button variant="ghost" size="sm" onClick={handleDuplicate} className="hidden sm:flex">
                                    <Copy className="w-4 h-4 mr-1" /> <span className="hidden lg:inline">Duplicate</span>
                                </Button>
                                <Button variant="ghost" size="sm" className="text-red-500 hidden sm:flex" onClick={handleDelete}>
                                    <Trash2 className="w-4 h-4 mr-1" /> <span className="hidden lg:inline">Delete</span>
                                </Button>
                                <Button 
                                    onClick={handleSave} 
                                    disabled={saving}
                                    size="sm"
                                    className="bg-brand-primary hover:bg-brand-primary-dark text-xs lg:text-sm"
                                >
                                    {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                                    Save
                                </Button>
                            </>
                        )}
                        {readOnly && (
                            <span className="text-xs text-brand-text-muted bg-brand-accent-soft px-2 py-1 rounded">
                                Mode Baca Saja
                            </span>
                        )}
                        <Button variant="ghost" size="icon" onClick={onClose} className="ml-1 lg:ml-2 h-8 w-8">
                            <X className="w-5 h-5" />
                        </Button>
                    </div>
                </div>

                {/* 3-Column Grid - Responsive */}
                <div className="flex-1 flex flex-col lg:grid lg:grid-cols-[300px_3fr_2fr] min-h-0 overflow-hidden">
                    
                    {/* LEFT COLUMN - Subtasks & Parent - Hidden on mobile, shown in tabs */}
                    <div className="hidden lg:flex border-r border-brand-border bg-gray-50/50 flex-col min-h-0">
                        <div className="p-4 border-b border-brand-border">
                            <h3 className="text-sm font-semibold text-brand-text-primary flex items-center gap-2">
                                <ListTree className="w-4 h-4" />
                                Subtasks
                            </h3>
                        </div>
                        
                        <ScrollArea className="flex-1" viewPortId="SubtasksScrollArea">
                            <div className="p-3 space-y-2 h-full w-full">
                                {/* Parent Task - Clickable to navigate */}
                                {parentTask && (
                                    <div className="mb-4">
                                        <p className="text-xs text-brand-text-muted mb-1">Parent Task</p>
                                        <div 
                                            className="p-2 bg-brand-accent-soft/50 rounded-lg border border-brand-border hover:border-brand-primary cursor-pointer transition-colors"
                                            onClick={handleParentClick}
                                        >
                                            <div className="flex items-center gap-2">
                                                <ChevronLeft className="w-4 h-4 text-brand-text-muted" />
                                                <span className="text-sm font-medium truncate">{parentTask.title}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Current Task Indicator */}
                                <div className="p-2 bg-brand-primary/10 rounded-lg border border-brand-primary/30">
                                    <div className="flex items-center gap-2">
                                        <CircleDot className="w-4 h-4 text-brand-primary" />
                                        <span className="text-sm font-medium text-brand-primary truncate">{title || 'Current Task'}</span>
                                    </div>
                                </div>

                                {/* Subtasks List - Clickable with Assignee Icons */}
                                {subtasks.length > 0 && (
                                    <div className="pl-4 space-y-1 mt-2">
                                        {subtasks.map(subtask => {
                                            const subtaskAssignees = getAssigneeInfo(subtask.assignee_ids);
                                            return (
                                                <div 
                                                    key={subtask.id}
                                                    className="flex items-center gap-2 p-2 hover:bg-brand-accent-soft rounded cursor-pointer group transition-colors"
                                                    onClick={() => handleSubtaskClick(subtask)}
                                                >
                                                    <Checkbox 
                                                        checked={subtask.status === 'Done'}
                                                        className="pointer-events-none"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleToggleSubtask(subtask);
                                                        }}
                                                    />
                                                    <span className={cn(
                                                        "text-sm flex-1 truncate",
                                                        subtask.status === 'Done' && "line-through text-brand-text-muted"
                                                    )}>
                                                        {subtask.title}
                                                    </span>
                                                    {/* Assignee Avatars */}
                                                    {subtaskAssignees && subtaskAssignees.length > 0 && (
                                                        <div className="flex -space-x-1">
                                                            {subtaskAssignees.slice(0, 2).map(assignee => (
                                                                <div 
                                                                    key={assignee.user_id || assignee.id}
                                                                    className="w-5 h-5 rounded-full bg-brand-primary text-white text-[9px] flex items-center justify-center border border-white"
                                                                    title={assignee.user_name || assignee.name}
                                                                >
                                                                    {getInitials(assignee.user_name || assignee.name)}
                                                                </div>
                                                            ))}
                                                            {subtaskAssignees.length > 2 && (
                                                                <div className="w-5 h-5 rounded-full bg-gray-300 text-gray-600 text-[9px] flex items-center justify-center border border-white">
                                                                    +{subtaskAssignees.length - 2}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    <ChevronRight className="w-4 h-4 text-brand-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Add Subtask */}
                                { !isDeletedTask && <div className="flex items-center gap-2 mt-3">
                                    <Plus className="w-4 h-4 text-brand-text-muted" />
                                    <Input
                                        value={newSubtaskTitle}
                                        onChange={(e) => setNewSubtaskTitle(e.target.value)}
                                        placeholder="Add Subtask"
                                        className="h-8 text-sm border-0 bg-transparent px-0 focus-visible:ring-0"
                                        onKeyDown={(e) => e.key === 'Enter' && handleAddSubtask()}
                                    />
                                </div>}
                            </div>
                        </ScrollArea>
                    </div>

                    {/* CENTER COLUMN - Task Details (Main on Mobile) */}
                    <div className="flex-1 flex flex-col min-h-0 min-w-0 order-first lg:order-none">
                        <ScrollArea className="flex-1 min-h-0 min-w-0">
                            <div className="p-4 lg:p-6 space-y-4 lg:space-y-6 min-w-0 max-w-full">
                            {/* Title */}
                            <div className="min-w-0 max-w-full">
                                <ResizableInput
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Task title"
                                    className="text-xl lg:text-2xl font-bold border-0 px-0 h-auto focus-visible:ring-0 bg-transparent w-full"
                                    style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap', maxWidth: '100%' }}
                                    disabled={readOnly}
                                    readOnly={readOnly}
                                />
                            </div>

                            {/* Mobile: Quick Actions */}
                            {!readOnly && (
                                <div className="flex lg:hidden gap-2 flex-wrap">
                                    <Button variant="outline" size="sm" onClick={handleDuplicate}>
                                        <Copy className="w-4 h-4" />
                                    </Button>
                                    <Button variant="outline" size="sm" className="text-red-500 border-red-200" onClick={handleDelete}>
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            )}

                            {/* Properties Grid */}
                            <div className="space-y-3">
                                {/* Status */}
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                    <div className="w-full sm:w-32 flex items-center gap-2 text-sm text-brand-text-muted">
                                        <CircleDot className="w-4 h-4" />
                                        Status
                                    </div>
                                    <FieldStatus
                                        status={status} 
                                        setStatus={(val) => {
                                            setStatus(val);
                                            handleInstantSave('status', val);
                                        }}
                                        statuses={listStatus}
                                        disabled={readOnly}
                                        className="w-full sm:w-fit min-w-40 h-8 border-0 bg-transparent hover:bg-brand-accent-soft"
                                        selectedAsChip={true}
                                    />
                                </div>

                                {/* Assignees */}
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                    <div className="w-full sm:w-32 flex items-center gap-2 text-sm text-brand-text-muted">
                                        <Users className="w-4 h-4" />
                                        Assignees
                                    </div>
                                    <div className="flex-1">
                                        <AssigneeMultiSelect
                                            members={members}
                                            selectedIds={assigneeIds}
                                            onChange={(ids) => {
                                                setAssigneeIds(ids);
                                                handleInstantSave('assignee_ids', ids);
                                            }}
                                            disabled={readOnly}
                                        />
                                    </div>
                                </div>

                                {/* Reviewers */}
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                    <div className="w-full sm:w-32 flex items-center gap-2 text-sm text-brand-text-muted">
                                        <Eye className="w-4 h-4" />
                                        Review By
                                    </div>
                                    <div className="flex-1">
                                        <AssigneeMultiSelect
                                            members={members}
                                            selectedIds={reviewerIds}
                                            onChange={(ids) => {
                                                setReviewerIds(ids);
                                                handleInstantSave('reviewer_ids', ids);
                                            }}
                                            disabled={readOnly}
                                        />
                                    </div>
                                </div>

                                {/* Dates */}
                                {/* Start Date & Due Date for Timeline (hidden for DAILY_TASK) */}
                                {category !== 'DAILY_TASK' && (
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                    <div className="w-full sm:w-32 flex items-center gap-2 text-sm text-brand-text-muted">
                                        <CalendarIcon className="w-4 h-4" />
                                        Timeline
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {/* Start Date */}
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button 
                                                    variant="outline" 
                                                    size="sm" 
                                                    className={cn(
                                                        "h-8 px-3",
                                                        !startDate && "text-brand-text-muted border-dashed"
                                                    )}
                                                >
                                                    <CalendarIcon className="w-3 h-3 mr-1.5" />
                                                    {startDate ? format(startDate, 'MMM dd, yyyy') : 'Start Date'}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                                <Calendar
                                                    mode="single"
                                                    selected={startDate}
                                                    onSelect={setStartDate}
                                                />
                                            </PopoverContent>
                                        </Popover>
                                        
                                        <ChevronRight className="w-4 h-4 text-brand-text-muted" />
                                        
                                        {/* Due Date */}
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button 
                                                    variant="outline" 
                                                    size="sm" 
                                                    className={cn(
                                                        "h-8 px-3",
                                                        !dueDate && "text-brand-text-muted border-dashed"
                                                    )}
                                                >
                                                    <CalendarIcon className="w-3 h-3 mr-1.5" />
                                                    {dueDate ? format(dueDate, 'MMM dd, yyyy') : 'Due Date'}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                                <Calendar
                                                    mode="single"
                                                    selected={dueDate}
                                                    onSelect={(newDate) => {
                                                        // If no existing due_date, just set directly (no reason needed)
                                                        const existing = currentTask?.due_date;
                                                        if (!existing) {
                                                            setDueDate(newDate);
                                                            return;
                                                        }
                                                        // If user cleared the date, keep original reason rule - clearing is a change, ask reason
                                                        const newFmt = newDate ? format(newDate, 'yyyy-MM-dd') : null;
                                                        if (newFmt === existing) {
                                                            setDueDate(newDate);
                                                            return;
                                                        }
                                                        // Existing date is being changed — prompt reason
                                                        setPendingDueDate(newDate);
                                                        setReasonDraft(dueDateChangeReason || '');
                                                        setShowDueDateReasonDialog(true);
                                                    }}
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                </div>
                                )}

                                {/* Priority */}
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                    <div className="w-full sm:w-32 flex items-center gap-2 text-sm text-brand-text-muted">
                                        <Flag className="w-4 h-4" />
                                        Priority
                                    </div>
                                    <Select 
                                        value={priority} 
                                        onValueChange={(val) => {
                                            setPriority(val);
                                            handleInstantSave('priority', val);
                                        }}
                                        disabled={readOnly}
                                    >
                                        <SelectTrigger className="w-full sm:w-32 h-8 border-0 bg-transparent hover:bg-brand-accent-soft">
                                            <SelectValue>
                                                {priority === 'none' ? (
                                                    <span className="text-brand-text-muted">Empty</span>
                                                ) : (
                                                    <span className={cn("px-2 py-0.5 rounded text-xs", priorityConfig[priority]?.bgClass)}>
                                                        {priorityConfig[priority]?.label}
                                                    </span>
                                                )}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(priorityConfig).map(([key, config]) => (
                                                <SelectItem key={key} value={key}>
                                                    <span className={cn("px-2 py-0.5 rounded text-xs", config.bgClass)}>
                                                        {config.label}
                                                    </span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Category */}
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                    <div className="w-full sm:w-32 flex items-center gap-2 text-sm text-brand-text-muted">
                                        <Bug className="w-4 h-4" />
                                        Category
                                    </div>
                                    <Select 
                                        value={category || 'none'} 
                                        onValueChange={(val) => {
                                            const newVal = val === 'none' ? '' : val;
                                            setCategory(newVal);
                                            handleInstantSave('category', newVal);
                                        }}
                                        disabled={readOnly}
                                    >
                                        <SelectTrigger className="w-full sm:w-40 h-8 border-0 bg-transparent hover:bg-brand-accent-soft">
                                            <SelectValue>
                                                {!category ? (
                                                    <span className="text-brand-text-muted">Empty</span>
                                                ) : (
                                                    <span className="px-2 py-0.5 rounded text-xs bg-brand-accent-soft text-brand-primary">
                                                        {category}
                                                    </span>
                                                )}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">
                                                <span className="text-brand-text-muted">None</span>
                                            </SelectItem>
                                            {taskCategories.map((cat) => (
                                                <SelectItem key={cat} value={cat}>
                                                    <span className="px-2 py-0.5 rounded text-xs bg-brand-accent-soft text-brand-primary">
                                                        {cat}
                                                    </span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Time Estimate - REMOVED per user request */}

                                {/* Track Time section removed - moved to header next to Copy Link */}

                                {/* Tags */}
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                    <div className="w-full sm:w-32 flex items-center gap-2 text-sm text-brand-text-muted">
                                        <Tag className="w-4 h-4" />
                                        Tags
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap flex-1">
                                        {tags.map(tag => (
                                            <span 
                                                key={tag}
                                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-800 text-white rounded text-xs"
                                            >
                                                {tag}
                                                <button onClick={() => removeTag(tag)} className="hover:text-red-300">
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                        <Input
                                            value={newTag}
                                            onChange={(e) => setNewTag(e.target.value)}
                                            placeholder="+ Add tag"
                                            className="w-24 h-6 text-xs border-0 bg-transparent px-1"
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                                        />
                                    </div>
                                </div>

                                {/* Attachments */}
                                <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
                                    <div className="w-full sm:w-32 flex items-center gap-2 text-sm text-brand-text-muted pt-1">
                                        <Paperclip className="w-4 h-4" />
                                        Attachments
                                    </div>
                                    <div className="flex-1">
                                        {attachments.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mb-2">
                                                {attachments.map(att => {
                                                    const ext = (att.filename || '').split('.').pop().toLowerCase();
                                                    const canEditOffice = officeEnabled && officeAllowed.includes(ext);
                                                    return (
                                                        <div key={att.id} className="flex items-center gap-1 px-2 py-1 bg-brand-accent-soft rounded text-xs max-w-full">
                                                            {getFileIcon(att.type || att.file_type)}
                                                            <a href={att.url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate max-w-[150px]">
                                                                {att.filename}
                                                            </a>
                                                            {canEditOffice && (
                                                                <button
                                                                    onClick={() => setOfficeFileId(att.id)}
                                                                    className="ml-1 text-brand-primary hover:underline font-medium"
                                                                    data-testid={`office-edit-${att.id}`}
                                                                    title="Edit dengan OnlyOffice"
                                                                >
                                                                    Edit
                                                                </button>
                                                            )}
                                                            <button onClick={() => setAttachments(attachments.filter(a => a.id !== att.id))} className="hover:text-red-500 flex-shrink-0">
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
                                        <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingFile}>
                                            {uploadingFile ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                                            Upload
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* Bug Analysis Section - Only for BUG category */}
                                {category === 'BUG' && (
                                    <div className="border-t border-brand-border pt-4 mt-4">
                                        <button
                                            type="button"
                                            onClick={() => setShowBugAnalysis(!showBugAnalysis)}
                                            className="flex items-center gap-2 text-sm font-medium text-brand-text mb-3 hover:text-brand-primary transition-colors"
                                        >
                                            <Bug className="w-4 h-4" />
                                            Bug Analysis & Prevention
                                            <ChevronRight className={cn("w-4 h-4 transition-transform", showBugAnalysis && "rotate-90")} />
                                        </button>
                                        
                                        {showBugAnalysis && (
                                            <div className="space-y-4 pl-6">
                                                {/* Root Cause Field */}
                                                <div>
                                                    <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                                                        Root Cause
                                                        {(status === 'Done' || status === 'Review') && (
                                                            <span className="text-xs text-red-500">*Required</span>
                                                        )}
                                                    </Label>
                                                    <Textarea
                                                        value={rootCause}
                                                        onChange={(e) => setRootCause(e.target.value)}
                                                        placeholder="Jelaskan akar masalah dari bug ini... (contoh: race condition pada async function, null pointer exception, dll)"
                                                        className="min-h-[100px] text-sm"
                                                        disabled={readOnly}
                                                    />
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Wajib diisi saat bug sudah Done/Review untuk AI learning
                                                    </p>
                                                </div>
                                                
                                                {/* Mitigations Field */}
                                                <div>
                                                    <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                                                        Mitigations
                                                        {(status === 'Done' || status === 'Review') && (
                                                            <span className="text-xs text-red-500">*Required</span>
                                                        )}
                                                    </Label>
                                                    <Textarea
                                                        value={mitigations}
                                                        onChange={(e) => setMitigations(e.target.value)}
                                                        placeholder="Jelaskan solusi yang diterapkan untuk memperbaiki bug ini... (contoh: menambahkan mutex lock, validasi input, dll)"
                                                        className="min-h-[100px] text-sm"
                                                        disabled={readOnly}
                                                    />
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Wajib diisi saat bug sudah Done/Review untuk AI learning
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Mobile: Subtasks Section */}
                            <div className="lg:hidden pt-4 border-t border-brand-border">
                                <Label className="text-sm text-brand-text-muted mb-2 flex items-center gap-2">
                                    <ListTree className="w-4 h-4" />
                                    Subtasks ({subtasks.length})
                                </Label>
                                <div className="space-y-2">
                                    {subtasks.map(subtask => (
                                        <div 
                                            key={subtask.id}
                                            className="flex items-center gap-2 p-2 bg-brand-accent-soft/30 rounded cursor-pointer"
                                            onClick={() => handleToggleSubtask(subtask)}
                                        >
                                            <Checkbox 
                                                checked={subtask.status === 'Done'}
                                                className="pointer-events-none"
                                            />
                                            <span className={cn(
                                                "text-sm flex-1 truncate",
                                                subtask.status === 'Done' && "line-through text-brand-text-muted"
                                            )}>
                                                {subtask.title}
                                            </span>
                                        </div>
                                    ))}
                                    <div className="flex items-center gap-2">
                                        <Plus className="w-4 h-4 text-brand-text-muted" />
                                        <Input
                                            value={newSubtaskTitle}
                                            onChange={(e) => setNewSubtaskTitle(e.target.value)}
                                            placeholder="Add Subtask"
                                            className="h-8 text-sm"
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddSubtask()}
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            {/* Similar Bugs Alert - Display above Description for BUG category */}
                            {category === 'BUG' && similarBugs && similarBugs.length > 0 && (
                                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-300 rounded-lg p-4 shadow-sm">
                                    <div className="flex items-start gap-3">
                                        <div className="flex-shrink-0">
                                            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                                                <Bug className="w-5 h-5 text-yellow-600" />
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-2">
                                                <h4 className="text-sm font-bold text-yellow-900">
                                                    ⚠️ Similar Bugs Detected by AI
                                                </h4>
                                                <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded-full font-medium">
                                                    {similarBugs.length} found
                                                </span>
                                            </div>
                                            <p className="text-xs text-yellow-800 mb-3">
                                                Review these similar bugs and their solutions before proceeding.
                                            </p>
                                            
                                            {/* Show only top 2 bugs */}
                                            <div className="space-y-2">
                                                {similarBugs.slice(0, 2).map((bug, idx) => (
                                                    <div key={idx} className="bg-white border border-yellow-200 rounded-lg p-3 shadow-sm">
                                                        <div className="flex items-start justify-between gap-2 mb-2">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                                    <span className="text-xs font-semibold text-gray-900 bg-gray-100 px-2 py-0.5 rounded">
                                                                        {bug.task_code || `Bug #${idx + 1}`}
                                                                    </span>
                                                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">
                                                                        {Math.round((bug.similarity_score || 0) * 100)}% match
                                                                    </span>
                                                                </div>
                                                                <p className="text-sm text-gray-800 font-medium line-clamp-1">{bug.title}</p>
                                                            </div>
                                                        </div>
                                                        
                                                        {bug.similarity_reasoning && (
                                                            <div className="text-xs text-gray-600 mb-2 italic bg-blue-50 p-2 rounded">
                                                                💡 <strong>Why similar:</strong> {bug.similarity_reasoning}
                                                            </div>
                                                        )}
                                                        
                                                        <div className="grid grid-cols-1 gap-2 mt-2">
                                                            {bug.root_cause && (
                                                                <div className="bg-red-50 p-2 rounded border border-red-100">
                                                                    <span className="text-xs font-semibold text-red-900 block mb-1">🔍 Root Cause:</span>
                                                                    <p className="text-xs text-red-800 line-clamp-2">{bug.root_cause}</p>
                                                                </div>
                                                            )}
                                                            
                                                            {bug.mitigations && (
                                                                <div className="bg-green-50 p-2 rounded border border-green-100">
                                                                    <span className="text-xs font-semibold text-green-900 block mb-1">✅ Solution Applied:</span>
                                                                    <p className="text-xs text-green-800 line-clamp-2">{bug.mitigations}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                        
                                                        {bug.ai_suggestion && (
                                                            <div className="bg-blue-100 border border-blue-200 rounded p-2 mt-2">
                                                                <span className="text-xs font-semibold text-blue-900 block mb-1">🤖 AI Recommendation:</span>
                                                                <p className="text-xs text-blue-800">{bug.ai_suggestion}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                            
                                            {similarBugs.length > 2 && (
                                                <p className="text-xs text-yellow-700 mt-2 text-center">
                                                    +{similarBugs.length - 2} more similar bug(s). Scroll down to Bug Analysis section for details.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Description */}
                            <div className="pt-4 border-t border-brand-border min-w-0 max-w-full">
                                <div className="flex items-center justify-between mb-2">
                                    <Label className="text-sm text-brand-text-muted">Description</Label>
                                    {!isEditingDescription && !readOnly && description && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setIsEditingDescription(true)}
                                            className="h-7 px-2 text-xs"
                                        >
                                            <Edit2 className="w-3 h-3 mr-1" />
                                            Edit
                                        </Button>
                                    )}
                                </div>
                                
                                {isEditingDescription && !readOnly ? (
                                    /* WYSIWYG Rich Text Editor */
                                    <div>
                                        <RichTextEditor
                                            value={description}
                                            onChange={(val) => setDescription(val || '')}
                                            placeholder="Write your description here... You can drag & drop images or click the image icon to upload."
                                            minHeight="150px"
                                        />
                                        {isEditingDescription && (
                                            <div className="flex justify-end mt-2 gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setIsEditingDescription(false)}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    onClick={() => {
                                                        setIsEditingDescription(false);
                                                        // Auto-save will be handled by main save
                                                    }}
                                                    className="bg-brand-primary hover:bg-brand-primary-dark"
                                                >
                                                    Done
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    /* Rich Text Viewer - Read Only */
                                    <div 
                                        className="prose prose-sm max-w-none p-3 bg-brand-accent-soft/20 rounded-lg min-h-[80px] cursor-pointer hover:bg-brand-accent-soft/40 transition-colors break-words [overflow-wrap:anywhere] [word-break:break-word] whitespace-pre-wrap overflow-hidden [&_*]:break-words [&_*]:![overflow-wrap:anywhere] [&_pre]:whitespace-pre-wrap [&_pre]:break-words [&_code]:break-words [&_a]:break-all [&_img]:max-w-full [&_img]:h-auto [&_table]:block [&_table]:overflow-x-auto"
                                        onClick={() => setIsEditingDescription(true)}
                                        title="Click to edit"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
                                    />
                                )}
                            </div>

                            {/* Checklist */}
                            <div className="pt-4 border-t border-brand-border">
                                <Label className="text-sm text-brand-text-muted mb-2 block">Checklist</Label>
                                <div className="space-y-2">
                                    {checklist.map((item, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <Checkbox
                                                checked={item.completed}
                                                onCheckedChange={() => toggleChecklistItem(index)}
                                            />
                                            <span className={cn("flex-1 text-sm", item.completed && "line-through text-brand-text-muted")}>
                                                {item.text}
                                            </span>
                                            { !readOnly && <button onClick={() => removeChecklistItem(index)} className="text-brand-text-muted hover:text-red-500">
                                                <Trash2 className="w-4 h-4" />
                                            </button>}
                                        </div>
                                    ))}
                                    { !readOnly && <div className="flex gap-2">
                                        <Input
                                            value={newChecklistItem}
                                            onChange={(e) => setNewChecklistItem(e.target.value)}
                                            placeholder="Add item"
                                            className="h-8"
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddChecklistItem()}
                                        />
                                        <Button variant="outline" size="sm" onClick={handleAddChecklistItem}>
                                            <Plus className="w-4 h-4" />
                                        </Button>
                                    </div>}
                                </div>
                            </div>

                            {/* Mobile: Activity Section */}
                            <div className="lg:hidden pt-4 border-t border-brand-border pb-4">
                                <Label className="text-sm text-brand-text-muted mb-3 flex items-center gap-2">
                                    <MessageSquare className="w-4 h-4" />
                                    Activity ({comments.length + timeLogs.length})
                                </Label>
                                
                                {/* Time Logs on Mobile */}
                                {timeLogs.length > 0 && (
                                    <div className="space-y-2 mb-4">
                                        {timeLogs.slice(0, 3).map(log => (
                                            <div key={log.id} className="flex items-start gap-2 text-sm p-2 bg-brand-accent-soft/30 rounded">
                                                <div className="w-1.5 h-1.5 rounded-full bg-brand-primary mt-2 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-brand-text-primary text-xs">
                                                        Logged {formatDuration(log.duration)}
                                                    </p>
                                                </div>
                                                <span className="text-xs text-brand-text-muted flex-shrink-0">{formatDate(log.logged_at)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Comments on Mobile */}
                                <ScrollArea className="flex-1">
                                    <div className="pb-4">
                                        {comments.map(comment => (
                                            <div key={comment.id} className="p-2 bg-brand-accent-soft/30 rounded mb-2">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-xs font-medium">{comment.user_name}</span>
                                                    <span className="text-xs text-brand-text-muted">{formatDate(comment.created_at)}</span>
                                                </div>
                                                <p className="text-sm mt-1">
                                                    <CommentRenderer 
                                                        text={comment.content} 
                                                        members={members}
                                                        attachments={comment.attachments || []}
                                                    />
                                                </p>
                                            </div>
                                        ))}

                                        {/* Comment Input Mobile */}
                                        { !readOnly && <div className="flex gap-2 mt-3">
                                            <Input
                                                value={newComment}
                                                onChange={(e) => setNewComment(e.target.value)}
                                                placeholder="Add comment..."
                                                className="text-sm"
                                                onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                                                disabled={submittingComment || readOnly}
                                            />
                                            <Button 
                                                onClick={handleAddComment} 
                                                size="icon"
                                                className="flex-shrink-0 bg-brand-primary hover:bg-brand-primary-dark"
                                                disabled={!newComment.trim() || submittingComment || readOnly}
                                            >
                                                <Send className="w-4 h-4" />
                                            </Button>
                                        </div>}
                                    </div>
                                </ScrollArea>
                            </div>
                            </div>
                        </ScrollArea>
                    </div>

                    {/* RIGHT COLUMN - Activity (Comments & Time Logs) - Hidden on Mobile */}
                    <div className="hidden lg:flex border-l border-brand-border flex-col bg-gray-50/30 h-full min-h-0">
                        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full min-h-0">
                            <TabsList className="w-[90%] mx-auto flex text-center flex-shrink-0">
                                <TabsTrigger value="comments" className="px-2 flex-1" >
                                    <h3 className="text-xs font-semibold text-brand-text-primary">Comments {comments.length > 0 && `(${comments.length})`}</h3>
                                </TabsTrigger>
                                <TabsTrigger value="activities" className="px-2 flex-1" >
                                    <h3 className="text-xs font-semibold text-brand-text-primary">Logs</h3>
                                </TabsTrigger>
                                {currentTask?.category === 'DAILY_TASK' && (
                                <TabsTrigger value="daily-status" className="px-2 flex-1">
                                    <h3 className="text-xs font-semibold text-brand-text-primary">Daily Status</h3>
                                </TabsTrigger>
                                )}
                            </TabsList>
                            <TabsContent value="comments" className="flex-1 flex flex-col min-h-0 mt-0 data-[state=inactive]:hidden">
                                <ScrollArea className="flex-1 min-h-0">
                                    <div className="flex flex-col px-4 py-2 space-y-4 w-full pb-4">
                                        {/* Time Logs */}
                                        {timeLogs.length > 0 && (
                                            <div className="space-y-2">
                                                {timeLogs.map(log => (
                                                    <div key={log.id} className="flex items-start gap-2 text-sm">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-brand-primary mt-2" />
                                                        <div className="flex-1">
                                                            <p className="text-brand-text-primary">
                                                                Logged {formatDuration(log.duration)}
                                                            </p>
                                                            {log.description && (
                                                                <p className="text-xs text-brand-text-muted">{log.description}</p>
                                                            )}
                                                        </div>
                                                        <span className="text-xs text-brand-text-muted">{formatDate(log.logged_at)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Comments */}
                                        {comments.map(comment => (
                                            <div key={comment.id} className="space-y-2 mb-2 mt-2">
                                                <div className="flex items-start gap-2">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2" />
                                                    <div className="w-full">
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm font-medium">{comment.user_name}</span>
                                                            <span className="text-xs text-brand-text-muted">{formatDate(comment.created_at)}</span>
                                                        </div>
                                                        {editingCommentId === comment.id ? (
                                                            <div className="mt-2 space-y-2">
                                                                <Textarea
                                                                    value={editCommentText}
                                                                    onChange={(e) => setEditCommentText(e.target.value)}
                                                                    rows={2}
                                                                    className="resize-none"
                                                                />
                                                                <div className="flex gap-2">
                                                                    <Button size="sm" onClick={() => handleUpdateComment(comment.id)}>Save</Button>
                                                                    <Button size="sm" variant="outline" onClick={() => { setEditingCommentId(null); setEditCommentText(''); }}>Cancel</Button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="w-full text-sm mt-1 whitespace-normal break-words">
                                                                <CommentRenderer 
                                                                    text={comment.content} 
                                                                    members={members}
                                                                    attachments={comment.attachments || []}
                                                                />
                                                            </div>
                                                        )}
                                                        {comment.author_id === user?.id && editingCommentId !== comment.id && (
                                                            <div className="flex gap-2 mt-1">
                                                                <button 
                                                                    onClick={() => { setEditingCommentId(comment.id); setEditCommentText(comment.content); }}
                                                                    className="text-xs text-brand-text-muted hover:text-brand-primary"
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button 
                                                                    onClick={() => handleDeleteComment(comment.id)}
                                                                    className="text-xs text-brand-text-muted hover:text-red-500"
                                                                >
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        {timeLogs.length === 0 && comments.length === 0 && (
                                            <p className="text-center text-brand-text-muted text-sm py-8">No activity yet</p>
                                        )}
                                    </div>
                                </ScrollArea>
                                {/* Comment Input */}
                                { !readOnly && <div className="p-4 border-t border-brand-border bg-white flex-shrink-0">
                                    <div className="flex gap-2">
                                        <InputComment
                                            members={members}
                                            onChange={handleOnchangeComment}
                                            ref={inputCommentRef}
                                        />
                                        <Button
                                            onClick={handleAddComment}
                                            size="icon"
                                            className="h-auto bg-brand-primary hover:bg-brand-primary-dark"
                                            disabled={!newComment.trim() || submittingComment || readOnly}
                                        >
                                            <Send className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>}

                            </TabsContent>
                            <TabsContent value="activities" className="flex-1 flex flex-col min-h-0 mt-0 data-[state=inactive]:hidden">
                                <ScrollArea className="flex-1 min-h-0">
                                    
                                    <div className="px-4 py-2 space-y-4">
                                         {/* Activities */}
                                        {activityLogs.map(log => (
                                            <div key={log.id} className="space-y-2 mb-2 mt-2">
                                                <div className="flex items-start gap-2">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2" />
                                                    <div className="flex-1">
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm font-medium">
                                                                {log.action === 'due_date_changed' 
                                                                    ? (log.changes?.message || `${log.user_name} mengubah duedate`)
                                                                    : `${log.user_name} ${log.action} ${log.entity_type}`}
                                                            </span>
                                                            <span className="text-xs text-brand-text-muted">{formatDateTime(log.created_at)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col gap-1 ml-3 text-xs">
                                                    {log.action === 'updated' && (
                                                        filteredChanges(log.changes).map((change) => (
                                                            change.value !== null && change.value.length > 0  && change.key !== 'updated_at' && change.key !== 'due_date_change_reason' && <div key={change.key} className="">
                                                                <span className="text-xs font-medium">{log.user_name} {log.action} </span> &nbsp; <span className="">{change.key.replace(/_/g, ' ')} to </span>
                                                                {change.key.includes('_ids') ? <CommentRenderer text={change.value} members={members} prefix={false} /> : <span className="text-xs ml-1 italic break-words">{renderChangesValue(change.value).slice(0, 20)}</span> }
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        ))}     
                                    </div>
                                </ScrollArea>
                            </TabsContent>
                            {currentTask?.category === 'DAILY_TASK' && (
                            <TabsContent value="daily-status" className="flex-1 flex flex-col min-h-0 mt-0 data-[state=inactive]:hidden">
                                <ScrollArea className="flex-1 min-h-0">
                                    <div className="px-4 py-3 space-y-2">
                                        <div className="text-xs font-semibold text-brand-text-muted mb-2">
                                            History Status Harian
                                        </div>
                                        {(currentTask?.daily_status_history || []).length === 0 ? (
                                            <p className="text-xs text-brand-text-muted italic">Belum ada history</p>
                                        ) : (
                                            [...currentTask.daily_status_history].reverse().map((entry, idx) => (
                                                <div key={idx} className="flex items-start gap-3 py-2 border-b border-brand-border/50">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs font-medium text-brand-text-primary">
                                                                {entry.date}
                                                            </span>
                                                            <span className={cn(
                                                                'text-xs px-2 py-0.5 rounded-full',
                                                                entry.status === 'Done' ? 'bg-green-100 text-green-700' :
                                                                entry.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                                                                entry.status === 'Review' ? 'bg-amber-100 text-amber-700' :
                                                                'bg-gray-100 text-gray-600'
                                                            )}>
                                                                {entry.status}
                                                            </span>
                                                        </div>
                                                        <p className="text-xs text-brand-text-muted mt-1">
                                                            {entry.changed_by_name} — {new Date(entry.changed_at).toLocaleString('id-ID')}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </ScrollArea>
                            </TabsContent>
                            )}
                        </Tabs>
                    </div>
                </div>
            </DialogContent>
            
            {/* Due Date Change Reason Dialog */}
            <Dialog open={showDueDateReasonDialog} onOpenChange={(open) => {
                if (!open) {
                    setShowDueDateReasonDialog(false);
                    setPendingDueDate(null);
                    setReasonDraft('');
                }
            }}>
                <DialogContent className="max-w-md" data-testid="due-date-reason-dialog">
                    <DialogHeader>
                        <DialogTitle>Alasan perubahan Due Date</DialogTitle>
                    </DialogHeader>
                    <div className="py-3 space-y-3">
                        <div className="text-sm text-brand-text-secondary">
                            <div>Dari: <span className="font-medium text-brand-text-primary">{currentTask?.due_date || '-'}</span></div>
                            <div>Menjadi: <span className="font-medium text-brand-text-primary">{pendingDueDate ? format(pendingDueDate, 'yyyy-MM-dd') : '-'}</span></div>
                        </div>
                        <Textarea
                            placeholder="Tulis alasan perubahan due date..."
                            value={reasonDraft}
                            onChange={(e) => setReasonDraft(e.target.value)}
                            rows={4}
                            data-testid="due-date-reason-input"
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => {
                            setShowDueDateReasonDialog(false);
                            setPendingDueDate(null);
                            setReasonDraft('');
                        }} data-testid="due-date-reason-cancel-btn">
                            Batal
                        </Button>
                        <Button onClick={() => {
                            const trimmed = reasonDraft.trim();
                            if (!trimmed) {
                                toast.error('Keterangan wajib diisi');
                                return;
                            }
                            setDueDateChangeReason(trimmed);
                            setDueDate(pendingDueDate);
                            setShowDueDateReasonDialog(false);
                            setPendingDueDate(null);
                            setReasonDraft('');
                            toast.info('Klik "Save" untuk menyimpan perubahan');
                        }} data-testid="due-date-reason-confirm-btn">
                            Lanjutkan
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* OnlyOffice Editor */}
            <OnlyOfficeEditorModal
                fileId={officeFileId}
                open={!!officeFileId}
                onClose={() => setOfficeFileId(null)}
            />
        </Dialog>
    );
};

export default TaskDetailFullModal;
